'use client';

import { c as _c } from "react/compiler-runtime";
import type { AiContext } from '@/components/chat/types/ai-context';
import type { SuggestedAction } from '@/data/chat/chat';
import { useQuery } from '@tanstack/react-query';
import { chatSuggestionsQueryKey } from './query-keys';
import { buildFileReviewDecisionsByFilename, formatFileReviewDecisionLabel, getReviewDecisionFilenames } from '@/lib/pr-v2/file-review-decisions';
const MAX_CHANGED_FILES = 18;
const MAX_REVIEW_GROUPS = 6;
const MAX_FILES_PER_GROUP = 3;
const MAX_REVIEW_DECISION_FILES = 8;
const MAX_FIELD_LENGTH = 2_400;
type SuggestionsContext = {
  prTitle: string;
  prDescription: string;
  owner: string;
  repo: string;
  prStats?: string;
  changedFilesSummary?: string;
  reviewPlanSummary?: string;
  reviewDecisionSummary?: string;
};
type FetchSuggestionsParams = {
  context: SuggestionsContext;
  installationId: number;
  prNumber: number;
  commitHash: string;
  signal?: AbortSignal;
};
type FileMetadata = {
  filename: string;
  status?: string;
  additions?: number;
  deletions?: number;
};
function truncateText(value: string, maxLength = MAX_FIELD_LENGTH): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
}
function buildFileMetadataMap(aiContext: AiContext): Map<string, FileMetadata> {
  const fileMetadataMap = new Map<string, FileMetadata>();
  for (const file of aiContext.codeContent ?? []) {
    fileMetadataMap.set(file.filename, {
      filename: file.filename,
      status: file.status,
      additions: file.additions,
      deletions: file.deletions
    });
  }
  for (const file of aiContext.files ?? []) {
    fileMetadataMap.set(file.filename, {
      filename: file.filename,
      status: file.status,
      additions: file.additions,
      deletions: file.deletions
    });
  }
  return fileMetadataMap;
}
function buildPrStats(aiContext: AiContext): string {
  const {
    pr
  } = aiContext;
  const reviewerCount = pr.reviewers.length;
  const approvedReviewerCount = pr.reviewers.filter(reviewer => reviewer.state === 'APPROVED').length;
  return [`${pr.changedFiles} files changed`, `+${pr.additions}`, `-${pr.deletions}`, `${pr.commits} commits`, pr.draft ? 'draft PR' : 'ready for review', reviewerCount > 0 ? `${approvedReviewerCount}/${reviewerCount} reviewers already approved` : 'no completed reviews yet'].join(' | ');
}
function buildChangedFilesSummary(aiContext: AiContext): string | undefined {
  const fileMetadataMap = buildFileMetadataMap(aiContext);
  const orderedFilenames = aiContext.orderedFiles?.map(file => file.filename);
  const filenames = (orderedFilenames?.length ? orderedFilenames : undefined) ?? (aiContext.codeContent?.length ? aiContext.codeContent.map(file => file.filename) : undefined) ?? (aiContext.files?.length ? aiContext.files.map(file => file.filename) : undefined) ?? [];
  if (filenames.length === 0) {
    return undefined;
  }
  const summaryLines = filenames.slice(0, MAX_CHANGED_FILES).map((filename, index) => {
    const file = fileMetadataMap.get(filename);
    if (!file) {
      return `${index + 1}. ${filename}`;
    }
    const changeParts = [file.status, typeof file.additions === 'number' ? `+${file.additions}` : null, typeof file.deletions === 'number' ? `-${file.deletions}` : null].filter(Boolean);
    return `${index + 1}. ${filename}${changeParts.length > 0 ? ` (${changeParts.join(', ')})` : ''}`;
  });
  if (filenames.length > MAX_CHANGED_FILES) {
    summaryLines.push(`+ ${filenames.length - MAX_CHANGED_FILES} more files`);
  }
  return truncateText(summaryLines.join('\n'));
}
function buildReviewPlanSummary(aiContext: AiContext): string | undefined {
  if (!aiContext.orderedFiles || aiContext.orderedFiles.length === 0) {
    return undefined;
  }
  const groupedFiles = new Map<string, {
    groupOrder: number;
    groupName: string;
    groupNameReason: string;
    files: NonNullable<AiContext['orderedFiles']>;
  }>();
  for (const file of aiContext.orderedFiles) {
    const groupKey = `${file.groupOrder}:${file.groupName}`;
    const existingGroup = groupedFiles.get(groupKey);
    if (existingGroup) {
      existingGroup.files.push(file);
      continue;
    }
    groupedFiles.set(groupKey, {
      groupOrder: file.groupOrder,
      groupName: file.groupName,
      groupNameReason: file.groupNameReason,
      files: [file]
    });
  }
  const reviewDecisionFilenames = getReviewDecisionFilenames(aiContext.reviewDecisions);
  const summaryLines = [...groupedFiles.values()].sort((left, right) => left.groupOrder - right.groupOrder).slice(0, MAX_REVIEW_GROUPS).map(group => {
    const sortedFiles = [...group.files].sort((left, right) => left.orderNumber - right.orderNumber);
    const hasReviewDecision = sortedFiles.some(file => reviewDecisionFilenames.has(file.filename));
    const filenames = sortedFiles.slice(0, MAX_FILES_PER_GROUP).map(file => file.filename).join(', ');
    return `${group.groupOrder}. ${group.groupName}${hasReviewDecision ? ' [decision]' : ''}: ${group.groupNameReason} Files: ${filenames}`;
  });
  return summaryLines.length > 0 ? truncateText(summaryLines.join('\n')) : undefined;
}
function buildReviewDecisionSummary(aiContext: AiContext): string | undefined {
  const reviewDecisions = aiContext.reviewDecisions ?? [];
  if (reviewDecisions.length === 0) {
    return undefined;
  }

  // Prefer the ordered-files sequence so the first bullets match the reviewer's
  // intended reading order; fall back to the decision order when the ordered-files
  // service hasn't landed yet.
  const orderedFilenames = [...(aiContext.orderedFiles && aiContext.orderedFiles.length > 0 ? [...aiContext.orderedFiles].sort((left, right) => left.orderNumber - right.orderNumber).map(file => file.filename) : []), ...reviewDecisions.map(decision => decision.filename)];
  const decisionsByFilename = buildFileReviewDecisionsByFilename(reviewDecisions);
  const seenFilenames = new Set<string>();
  const bullets: string[] = [];
  for (const filename of orderedFilenames) {
    if (seenFilenames.has(filename)) {
      continue;
    }
    seenFilenames.add(filename);
    const decisions = decisionsByFilename.get(filename);
    if (!decisions || decisions.length === 0) {
      continue;
    }
    const label = formatFileReviewDecisionLabel(decisions, {
      separator: ' | '
    });
    bullets.push(label ? `- ${filename}: ${label}` : `- ${filename}`);
    if (bullets.length >= MAX_REVIEW_DECISION_FILES) {
      break;
    }
  }
  return bullets.length > 0 ? truncateText(bullets.join('\n')) : undefined;
}

/**
 * Extracts and summarises PR metadata from the full AI context into the
 * compact shape expected by the chat-suggestions endpoint.
 *
 * @param aiContext - Full AI context containing PR data, ordered files, and review plan
 * @returns Structured suggestions context with PR stats and file ordering summaries
 */
export function buildChatSuggestionsContext(aiContext: AiContext): SuggestionsContext {
  return {
    prTitle: aiContext.pr.title,
    prDescription: aiContext.pr.body,
    owner: aiContext.pr.repository.owner,
    repo: aiContext.pr.repository.name,
    prStats: buildPrStats(aiContext),
    changedFilesSummary: buildChangedFilesSummary(aiContext),
    reviewPlanSummary: buildReviewPlanSummary(aiContext),
    reviewDecisionSummary: buildReviewDecisionSummary(aiContext)
  };
}
export const fetchChatSuggestions = async ({
  signal,
  ...body
}: FetchSuggestionsParams): Promise<SuggestedAction[]> => {
  const response = await fetch('/api/chat/suggestions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      context: body.context,
      cacheKeyData: {
        installationId: body.installationId,
        prNumber: body.prNumber,
        commitHash: body.commitHash
      }
    }),
    signal
  });
  if (!response.ok) {
    throw new Error('Failed to fetch chat suggestions');
  }
  const data = await response.json();
  return data.suggestions;
};
export const useChatSuggestions = (params, options) => {
  const $ = _c(10);
  const {
    installationId,
    prNumber,
    commitHash
  } = params;
  const isEnabled = options?.enabled ?? Boolean(installationId && prNumber && commitHash);
  let t0;
  if ($[0] !== commitHash || $[1] !== installationId || $[2] !== prNumber) {
    t0 = chatSuggestionsQueryKey(installationId, prNumber, commitHash);
    $[0] = commitHash;
    $[1] = installationId;
    $[2] = prNumber;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== params) {
    t1 = t2 => {
      const {
        signal
      } = t2;
      return fetchChatSuggestions({
        ...params,
        signal
      });
    };
    $[4] = params;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== isEnabled || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      enabled: isEnabled,
      placeholderData: _temp
    };
    $[6] = isEnabled;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useQuery(t2);
};
function _temp() {}